import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Slider,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import { getDifferentValues } from "../../services/utils/processRuleExecutionResourcesData";
import {
  createValidationScoringConfig,
  getValidationScoringConfigById,
  updateValidationScoringConfig,
} from "../../services/scoringService";
import useFetchValidationScoringConfigById from "../../hooks/useFetchValidationScoringConfigById";
import { validationScoringConfigDefaults } from "../../services/constants";
import { validationScoringConfigFormSchema } from "../../schemas";
import {
  getImpactSumError,
  isScoringSubmitDisabled,
} from "../../utils/scoringConfigFormValidation";
import { SLIDER_ACCENT } from "../../components/Scoring/scoreTheme";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import AutoCompleteDomainList from "../../components/Molecules/Domain/AutoCompleteDomainList";
import AutoCompleteResourceList from "../../components/Molecules/Resource/AutoCompleteResourceList";

const SLIDER_SX = {
  color: SLIDER_ACCENT,
  "& .MuiSlider-thumb": { color: SLIDER_ACCENT },
  "& .MuiSlider-track": { color: SLIDER_ACCENT, border: "none" },
  "& .MuiSlider-rail": { color: SLIDER_ACCENT, opacity: 0.3 },
  "& .MuiSlider-valueLabel": { backgroundColor: SLIDER_ACCENT },
  "& .MuiSlider-markActive": { backgroundColor: SLIDER_ACCENT },
};

const SLIDER_MARKS = [
  { value: 0, label: "0" },
  { value: 0.5, label: "0.5" },
  { value: 1, label: "1" },
];

const AddEditValidationScoring: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id: configId } = useParams();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const isEditMode = Boolean(configId);
  const [isLoading, setIsLoading] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [currentDomainId, setCurrentDomainId] = useState<number | null>(null);
  const [domainError, setDomainError] = useState<string>("");
  const [resourceId, setResourceId] = useState<number | "">("");
  const [resourceError, setResourceError] = useState<string>("");

  const [configData] = useFetchValidationScoringConfigById({
    setIsLoading,
    configId: configId ? parseInt(configId) : undefined,
  });
  const [localConfigOverride, setLocalConfigOverride] = useState<any>(null);
  const effectiveConfig = localConfigOverride || configData;
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  const formik = useFormik({
    initialValues: {
      impact_of_record_level:
        effectiveConfig?.impact_of_record_level ??
        validationScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        effectiveConfig?.impact_of_issue_level ??
        validationScoringConfigDefaults.impact_of_issue_level,
      factor_low:
        effectiveConfig?.factor_low ??
        validationScoringConfigDefaults.factor_low,
      factor_medium:
        effectiveConfig?.factor_medium ??
        validationScoringConfigDefaults.factor_medium,
      factor_high:
        effectiveConfig?.factor_high ??
        validationScoringConfigDefaults.factor_high,
    },
    validationSchema: validationScoringConfigFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!isEditMode) {
        let hasError = false;
        if (!currentDomainId) {
          setDomainError("Domain is required");
          hasError = true;
        }
        if (!resourceId) {
          setResourceError("Resource ID is required");
          hasError = true;
        }
        if (hasError) return;
        setIsLoading(true);
        try {
          await createValidationScoringConfig({
            resource_id: Number(resourceId),
            impact_of_record_level: values.impact_of_record_level,
            impact_of_issue_level: values.impact_of_issue_level,
            factor_low: values.factor_low,
            factor_medium: values.factor_medium,
            factor_high: values.factor_high,
          });
          showToast("Validation scoring config created!", "success");
          navigate("/scoring-config?tab=0&runSub=0");
        } catch (error) {
          showToast(
            extractApiErrorMessage(error, "Failed to create config"),
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        const diff = getDifferentValues(values, formik.initialValues);
        if (!diff || Object.keys(diff).length === 0) {
          showToast("No changes detected", "warning");
          return;
        }
        setPendingPayload(values);
        setOpenComment(true);
      }
    },
  });

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: isEditMode ? "Edit Validation Scoring" : "Add Validation Scoring",
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  useEffect(() => {
    if (effectiveConfig?.resource_id) {
      setResourceId(effectiveConfig.resource_id);
    }
  }, [effectiveConfig]);

  const buildPayload = () => {
    if (isEditMode) {
      // Mirrors the body sent by handleSaveComment to updateValidationScoringConfig.
      // Uses the live `comment` state so the View JSON always reflects the
      // exact payload that would be sent if Save were clicked right now.
      // Include resource_id so the payload can be used by Config application.
      return {
        resource_id: Number(resourceId),
        impact_of_record_level: formik.values.impact_of_record_level,
        impact_of_issue_level: formik.values.impact_of_issue_level,
        factor_low: formik.values.factor_low,
        factor_medium: formik.values.factor_medium,
        factor_high: formik.values.factor_high,
        comment,
      };
    }
    return {
      resource_id: Number(resourceId),
      impact_of_record_level: formik.values.impact_of_record_level,
      impact_of_issue_level: formik.values.impact_of_issue_level,
      factor_low: formik.values.factor_low,
      factor_medium: formik.values.factor_medium,
      factor_high: formik.values.factor_high,
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/validation-scoring-config/${configId}`
          : "/validation-scoring-config",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: configId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) showToast("Payload is valid!", "success");
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(buildPayload(), null, 2)
    );
    showToast("Payload copied to clipboard!", "success");
  };

  const handelChangeDomain = (_event: any, value: any) => {
    setCurrentDomainId(value?.id ?? null);
    setResourceId("");
    if (domainError) setDomainError("");
    if (resourceError) setResourceError("");
  };

  const handelChangeResource = (_event: any, value: any) => {
    setResourceId(value?.id ?? "");
    if (resourceError) setResourceError("");
  };

  const handleSaveComment = async () => {
    setOpenComment(false);
    setIsLoading(true);
    try {
      const payload: any = {};
      Object.keys(pendingPayload).forEach((k) => {
        const val = pendingPayload[k];
        payload[k] = isNaN(Number(val)) ? val : Number(val);
      });
      await updateValidationScoringConfig(parseInt(configId!), {
        ...payload,
        comment,
      });
      showToast("Config updated!", "success");
      // Refetch from server so enableReinitialize picks up fresh data
      const refreshed = await getValidationScoringConfigById(
        parseInt(configId!)
      );
      if (refreshed) {
        setLocalConfigOverride(refreshed);
      }
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update config"),
        "error"
      );
    } finally {
      setIsLoading(false);
      setComment("");
      setPendingPayload(null);
    }
  };

  const handleResetToDefaults = () => {
    formik.setValues({
      impact_of_record_level:
        validationScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        validationScoringConfigDefaults.impact_of_issue_level,
      factor_low: validationScoringConfigDefaults.factor_low,
      factor_medium: validationScoringConfigDefaults.factor_medium,
      factor_high: validationScoringConfigDefaults.factor_high,
    });
  };

  /**
   * Custom onChange / onBlur for numeric <input type="number"> fields.
   *
   * DQ-8556: non-empty NaN (intermediate browser state) leaves Formik untouched
   * so the browser input buffer persists.
   * DQ-8521: explicit empty string (user cleared all digits) is committed to
   * Formik so the field does not snap back and Yup validation fires on blur.
   */
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "") {
      formik.setFieldValue(name, "");
      return;
    }
    const v = parseFloat(value);
    if (!isNaN(v)) {
      formik.setFieldValue(name, v);
    }
  };

  const handleNumericBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    if (isNaN(parseFloat(e.target.value))) {
      formik.setFieldValue(e.target.name, "");
    }
  };

  const impactSumError = getImpactSumError(formik.errors);

  const factorField = (
    label: string,
    name: "factor_low" | "factor_medium" | "factor_high"
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <label className="label-text">
        {label}
        <span className="required-asterisk">*</span>
      </label>
      <TextField
        name={name}
        type="number"
        inputProps={{ step: 0.05 }}
        value={formik.values[name]}
        onChange={handleNumericChange}
        onBlur={handleNumericBlur}
        className={`form-control ${formik.errors[name] ? "has-error" : ""}`}
        error={Boolean(formik.errors[name])}
        helperText={(formik.errors[name] as string) || " "}
      />
    </Grid>
  );

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header">
              <h3>{isEditMode ? "Edit" : "Add"} Validation Scoring Config</h3>
            </Box>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              {!isEditMode && (
                <Grid item xs={12} sm={6} md={4}>
                  <AutoCompleteDomainList
                    setIsLoading={setIsLoading}
                    handelOnChangeDomain={handelChangeDomain}
                    currentDomainId={currentDomainId}
                    className={`form-control-autocomplete ${
                      domainError ? "has-error" : ""
                    }`}
                    required
                    error={Boolean(domainError)}
                    helperText={domainError}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                {isEditMode ? (
                  <>
                    <label className="label-text">Resource</label>
                    <TextField
                      fullWidth
                      value={effectiveConfig?.resource_name || ""}
                      InputProps={{ readOnly: true }}
                      className="form-control"
                    />
                  </>
                ) : (
                  <AutoCompleteResourceList
                    setIsLoading={setIsLoading}
                    handelOnChangeResource={handelChangeResource}
                    currentResourceId={resourceId || null}
                    currentDomainId={currentDomainId}
                    className={`form-control-autocomplete ${
                      resourceError ? "has-error" : ""
                    }`}
                    required
                    error={Boolean(resourceError)}
                    helperText={resourceError}
                    isDisabled={!currentDomainId}
                    filterByScoreConfigAssociated
                  />
                )}
              </Grid>
            </Grid>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
              sx={{ mt: "20px" }}
            >
              <Grid item xs={12} md={6}>
                <label className="label-text">
                  Impact of Record Level:{" "}
                  {(Number(formik.values.impact_of_record_level) || 0).toFixed(
                    2
                  )}
                </label>
                <Slider
                  name="impact_of_record_level"
                  value={Number(formik.values.impact_of_record_level) || 0}
                  onChange={(_, val) =>
                    formik.setFieldValue("impact_of_record_level", val)
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  marks={SLIDER_MARKS}
                  valueLabelDisplay="auto"
                  sx={SLIDER_SX}
                />
                {formik.touched.impact_of_record_level &&
                  formik.errors.impact_of_record_level &&
                  formik.errors.impact_of_record_level !== impactSumError && (
                    <Typography color="error" variant="caption">
                      {formik.errors.impact_of_record_level as string}
                    </Typography>
                  )}
              </Grid>
              <Grid item xs={12} md={6}>
                <label className="label-text">
                  Impact of Issue Level:{" "}
                  {(Number(formik.values.impact_of_issue_level) || 0).toFixed(
                    2
                  )}
                </label>
                <Slider
                  name="impact_of_issue_level"
                  value={Number(formik.values.impact_of_issue_level) || 0}
                  onChange={(_, val) =>
                    formik.setFieldValue("impact_of_issue_level", val)
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  marks={SLIDER_MARKS}
                  valueLabelDisplay="auto"
                  sx={SLIDER_SX}
                />
                {formik.touched.impact_of_issue_level &&
                  formik.errors.impact_of_issue_level &&
                  formik.errors.impact_of_issue_level !== impactSumError && (
                    <Typography color="error" variant="caption">
                      {formik.errors.impact_of_issue_level as string}
                    </Typography>
                  )}
              </Grid>
              {impactSumError && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {impactSumError}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
              sx={{ mt: "20px" }}
            >
              {factorField("Factor (Low)", "factor_low")}
              {factorField("Factor (Medium)", "factor_medium")}
              {factorField("Factor (High)", "factor_high")}
            </Grid>
          </Box>
          <FlexBetween gap={2.5} sx={{ mt: 1, justifyContent: "flex-end" }}>
            <Button
              type="button"
              className="btn-orange btn-dark"
              variant="contained"
              disabled={formik.isSubmitting}
              onClick={handleResetToDefaults}
              startIcon={<RestoreIcon />}
            >
              Reset to Defaults
            </Button>
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isScoringSubmitDisabled({
                isSubmitting: formik.isSubmitting,
                isValid: formik.isValid,
                isEditMode,
                hasChanges: formik.dirty,
              })}
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
            >
              Save
            </Button>
          </FlexBetween>
        </form>
      </Box>
      <CommentBeforeUpdateDialog
        title="Confirm updating Validation Scoring Config"
        dialogContent={
          <div>
            <p className="m-0 mb-2">Add a note before updating</p>
            <textarea
              value={comment}
              className="form-control-1 max-60"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        }
        openConfirmation={openComment}
        handleSaveComment={handleSaveComment}
        handleCancel={() => {
          setOpenComment(false);
          setComment("");
          setPendingPayload(null);
        }}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Validation Scoring Config Payload"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default AddEditValidationScoring;
